'use client';
/**
 * LES DEMANDES DE CRÉATION DE COMPTE — la porte unique.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * ── Pourquoi ce composant existe (13/09/2026) ──────────────────────────────
 *
 * Ces demandes avaient DEUX portes. L'écran « Demandes reçues » du site les
 * listait, avec ses boutons Valider et Refuser, son suivi et son journal ; et
 * l'écran Utilisateurs de la Qualité en listait les mêmes, avec ses propres
 * boutons. Un même serveur derrière, aucune concertation entre les deux : deux
 * personnes pouvaient trancher la même demande, et la seconde trouvait un état
 * déjà changé sans savoir par qui. (Retour 013 de la RQ, 11/09/2026.)
 *
 * Arbitrage : **la Qualité répond et consulte, un seul endroit.** Le site perd
 * la chose entièrement — pas seulement les boutons — et garde les rendez-vous,
 * qu'il est seul à produire.
 *
 * ⚠️ CE COMPOSANT DOIT SAVOIR TOUT CE QUE L'INBOX DU SITE SAVAIT FAIRE, sans
 * quoi « une seule porte » voudrait dire « une capacité en moins ». Mesuré en
 * base avant la bascule : 6 demandes, toutes refusées — donc INVISIBLES dans
 * la Qualité, qui ne listait que les demandes en attente — et 1 commentaire
 * interne, qui serait devenu inatteignable. D'où, ici : l'historique, la
 * remise en attente, le suivi et le journal.
 *
 * ── La distinction que cet écran refuse de perdre ──────────────────────────
 *
 * « AUCUNE DEMANDE » ≠ « JE N'AI PAS PU LIRE ». `demandes` est un tri-état :
 * `null` signifie que la lecture a échoué, et l'écran le DIT au lieu d'afficher
 * une liste vide. C'est ce défaut précis qui a laissé le 404 du proxy vivre
 * plusieurs semaines sans que personne le voie.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { I, card, Modale, EmptyState, useBusy } from '@/components/ui';
import {
  listRequests, approveRequest, rejectRequest, reopenRequest,
  type AccessRequest, type VerdictEnvoi,
} from '@/lib/team';
import { listerNotes, ajouterNote, journalDe, type NoteDemande, type LigneJournal } from '@/lib/suivi-demande';

import { Bouton } from '@/shared/Bouton';
import { fmtDateHeure } from '@/lib/meta';
import { ChoixCorrespondant, useNomsContacts, type ContactBref } from '@/components/ChoixCorrespondant';
import type { LienCarnet } from '@/lib/team';
import { nomsProches } from '@/lib/rapprochement-nom';
const STATUT_DEMANDE: Record<string, { label: string; bg: string; fg: string }> = {
  pending: { label: 'En attente', bg: 'var(--cyan-50)', fg: 'var(--cyan-700)' },
  approved: { label: 'Validée', bg: 'var(--green-50)', fg: 'var(--green-600)' },
  rejected: { label: 'Refusée', bg: 'var(--red-50)', fg: 'var(--red-600)' },
};

const profilDemande = (r: AccessRequest) => (r.role === 'staff' ? 'Personnel (ancienne demande)' : 'Correspondant');
/** Une demande « personnel » déposée avant le 16/10/2026 : elle ne se valide
 *  plus ici, le compte d'un salarié s'ouvre depuis son dossier RH. */
const estPersonnel = (r: AccessRequest) => r.role === 'staff';
/** Le profil que l'approbation posera. Écrit ICI et nulle part ailleurs : la
 *  même règle vit côté moteur, et un écran qui en aurait sa propre version
 *  finirait par annoncer autre chose que ce qui sera créé. */
const profilCible = (r: AccessRequest) => (r.role === 'staff' ? 'technician' : 'partner');

function Pastille({ statut }: { statut: string }) {
  const s = STATUT_DEMANDE[statut] || STATUT_DEMANDE.rejected;
  return (
    <span style={{ display: 'inline-block', padding: '2px 9px', borderRadius: 'var(--radius-pill)', background: s?.bg, color: s?.fg, fontSize: 11, fontWeight: 700 }}>{s?.label}</span>
  );
}

/** Une ligne de la liste. Cliquable sur toute sa surface : c'est le geste
 *  attendu, et il n'existait nulle part en Qualité — on ne pouvait voir ni le
 *  téléphone, ni la ville, ni le suivi d'une demande. */
function LigneDemande({ r, onOuvrir, actions }: {
  r: AccessRequest; onOuvrir: () => void; actions?: React.ReactNode;
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', padding: '11px 16px', borderTop: '1px solid var(--border-default)', background: 'var(--surface-card)' }}>
      <button type="button" onClick={onOuvrir}
        style={{ flex: 1, minWidth: 200, display: 'block', textAlign: 'start', background: 'none', border: 'none', padding: 0, cursor: 'pointer', font: 'inherit' }}>
        <span style={{ display: 'block', fontSize: 13.5, fontWeight: 600, color: 'var(--text-strong)' }}>
          {r.prenom} {r.nom}{' '}
          <span style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--cyan-700)' }}>· {profilDemande(r)}</span>
        </span>
        <span style={{ display: 'block', fontSize: 12, color: 'var(--text-muted)' }}>
          {[r.typeLibelle, r.institution, r.fonction, r.email, r.date].filter(Boolean).join(' · ')}
        </span>
      </button>
      {actions && <div style={{ display: 'flex', gap: 8, flex: 'none', alignItems: 'center' }}>{actions}</div>}
    </div>
  );
}

const ligneSuivi: React.CSSProperties = {
  padding: '9px 12px', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-default)',
  background: 'var(--gray-25)', fontSize: 12.5, lineHeight: 1.55, color: 'var(--text-body)',
};

const horodate = (v?: string) => (v ? fmtDateHeure(v) : '');

/**
 * LA FICHE D'UNE DEMANDE — tout ce que la liste ne montre pas.
 *
 * ⚠️ `sansSaisie` N'EST PAS POSÉ : on écrit ici (le champ de suivi). La croix
 * doit donc demander confirmation si quelque chose est en cours de frappe —
 * c'est la règle de la maison sur la sortie des fenêtres, et la seule chose
 * qu'on puisse perdre dans cet écran est précisément ce texte-là.
 */
/** Les fiches de l'annuaire dont le nom ressemble à l'institution déclarée (recherche de l'annuaire). */
function useCandidatsAnnuaire(texte: string, actif: boolean): { lignes: { code: string; nom: string; actif: boolean }[]; chargement: boolean; echec: boolean } {
  const [etat, setEtat] = useState<{ texte: string; lignes: { code: string; nom: string; actif: boolean }[]; echec: boolean } | null>(null);
  useEffect(() => {
    if (!actif || texte.trim().length < 2) return;
    let vivant = true;
    void fetch(`/api/ged/ged/annuaire/contacts?q=${encodeURIComponent(texte)}`, { credentials: 'include' })
      .then((x) => (x.ok ? x.json() : Promise.reject(new Error(String(x.status)))))
      .then((j: { contacts: ContactBref[] }) => { if (vivant) setEtat({ texte, lignes: (j.contacts || []).map((c) => ({ code: c.id, nom: c.nom, actif: c.actif })), echec: false }); })
      .catch(() => { if (vivant) setEtat({ texte, lignes: [], echec: true }); });
    return () => { vivant = false; };
  }, [texte, actif]);
  if (!actif || texte.trim().length < 2) return { lignes: [], chargement: false, echec: false };
  return etat && etat.texte === texte ? { lignes: etat.lignes, chargement: false, echec: etat.echec } : { lignes: [], chargement: true, echec: false };
}

function FicheDemande({ r, onClose, onDecider, occupe }: {
  r: AccessRequest;
  onClose: () => void;
  onDecider: (geste: 'approve' | 'reject' | 'reopen', lien?: LienCarnet) => void;
  occupe: boolean;
}) {
  const ref = String(r.ref || '');
  /* ── Le lien à l'ANNUAIRE (16/10/2026 ; le carnet est supprimé le 20/10/2026) ──────────────────
     Les fiches au nom PROCHE sont proposées en premier (« Labo Ben Ali » / « Laboratoire Ben Ali — Sousse »),
     trouvées par la recherche de l'annuaire ; la RQ décide : relier à l'une d'elles, relier à une autre fiche, ou
     créer une nouvelle fiche. Quand une proposition existe, rien n'est présélectionné : la décision est à elle,
     pas à l'écran. Sans proposition, « créer » est le seul choix. */
  const enAttente = r.status === 'pending' && !estPersonnel(r);
  const trouvees = useCandidatsAnnuaire(String(r.institution || ''), enAttente);
  const candidats = useMemo(() => nomsProches(String(r.institution || ''), trouvees.lignes), [trouvees.lignes, r.institution]);
  const [decision, setDecision] = useState<string | null>(null); // code du contact, 'creer', ou null (à décider)
  const decisionEffective = decision ?? (!trouvees.chargement && !trouvees.echec && !candidats.length ? 'creer' : null);
  const lien: LienCarnet | null = decisionEffective === 'creer'
    ? { creerCorrespondant: true }
    : decisionEffective ? { correspondantCode: decisionEffective } : null;
  const codesCandidats = useMemo(() => candidats.map((c) => c.code), [candidats]);
  const autreChoisi = decisionEffective && decisionEffective !== 'creer' && !codesCandidats.includes(decisionEffective) ? decisionEffective : '';
  const nomsLies = useNomsContacts(r.correspondantCode ? [String(r.correspondantCode)] : []);
  const [notes, setNotes] = useState<NoteDemande[] | null>(null);
  const [journal, setJournal] = useState<LigneJournal[] | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [texte, setTexte] = useState('');
  const [type, setType] = useState<'commentaire' | 'reponse'>('commentaire');
  const [avis, setAvis] = useState<string | null>(null);
  const busy = useBusy();
  const occupeSuivi = busy.estOccupe('suivi');

  /* Une demande SANS référence n'a ni suivi ni journal : les deux s'y
     rattachent par elle. On le dit, plutôt que d'afficher deux listes
     vides qui se liraient « rien ne s'est passé ». Dérivé au rendu, pas
     posé par l'effet : rien n'est à lire, il n'y a donc rien à attendre. */
  const erreurSuivi = ref ? erreur : 'Cette demande n’a pas de référence : son suivi et son journal ne peuvent pas être retrouvés.';
  const charger = useCallback(() => {
    if (!ref) return;
    void Promise.all([listerNotes(ref), journalDe(ref)])
      .then(([n, j]) => { setNotes(n); setJournal(j); setErreur(null); })
      .catch((e) => setErreur(e instanceof Error ? e.message : 'Lecture impossible'));
  }, [ref]);
  useEffect(charger, [charger]);

  const envoyer = () => {
    const t = texte.trim();
    if (!t) return;
    setAvis(null);
    void busy.run('suivi', async () => {
      const res = await ajouterNote({ cible: 'compte', cibleRef: ref, type, texte: t });
      setTexte('');
      charger();
      /* ⚠️ UNE RÉPONSE ENREGISTRÉE N'EST PAS UNE RÉPONSE REÇUE. Elle est
         conservée même quand le courriel échoue — la perdre effacerait le
         travail de quelqu'un — mais la présenter comme partie ferait croire le
         demandeur prévenu. */
      if (type === 'reponse') {
        setAvis(res?.envoi === 'envoye'
          ? `Réponse envoyée à ${r.email}.`
          : `Réponse ENREGISTRÉE, mais le courriel n’est PAS parti : ${r.prenom} ${r.nom} ne l’a pas reçue. Prévenez la personne autrement.`);
      } else {
        setAvis('Commentaire interne ajouté — il n’est pas envoyé au demandeur.');
      }
    }).catch((e) => setAvis(e instanceof Error ? e.message : 'Enregistrement refusé.'));
  };

  const champ = (libelle: string, valeur?: string) => (valeur ? (
    <div style={{ display: 'flex', gap: 10, fontSize: 12.5, lineHeight: 1.6 }}>
      <span style={{ minWidth: 96, color: 'var(--text-muted)' }}>{libelle}</span>
      <span style={{ color: 'var(--text-body)', fontWeight: 500 }}>{valeur}</span>
    </div>
  ) : null);

  return (
    <Modale titre={`${r.prenom} ${r.nom}`.trim() || r.email}
      sousTitre={<span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}><Pastille statut={r.status} />{ref && <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11.5, color: 'var(--text-muted)' }}>{ref}</span>}</span>}
      onClose={onClose} largeur={660}
      pied={
        r.status === 'pending' ? (
          <>
            <Bouton variante="danger" taille="sm" type="button" disabled={occupe} onClick={() => onDecider('reject')}>Refuser</Bouton>
            <Bouton variante="primaire" taille="sm" type="button" disabled={occupe || estPersonnel(r) || !lien}
              title={estPersonnel(r) ? 'Le compte d’un membre du personnel s’ouvre depuis son dossier RH.'
                : !lien ? 'Décidez d’abord : relier à une fiche de l’annuaire, ou créer une nouvelle fiche.' : undefined}
              onClick={() => { if (lien) onDecider('approve', lien); }}>Valider &amp; inviter</Bouton>
          </>
        ) : r.status === 'rejected' ? (
          <Bouton taille="sm" type="button" disabled={occupe} onClick={() => onDecider('reopen')}>Remettre en attente</Bouton>
        ) : (
          /* Une demande validée a CRÉÉ un compte. « Remettre en attente » ne le
             défait pas : ce serait un bouton qui ment sur son effet. Ce qui se
             fait, c'est suspendre le compte — dans la liste, juste derrière. */
          <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>Le compte est créé — cette décision ne se défait pas ici.</span>
        )
      }>
      <div style={{ display: 'grid', gap: 6, marginBottom: 18 }}>
        {champ('Profil demandé', profilDemande(r))}
        {champ('Type', r.typeLibelle)}
        {champ('Institution', r.institution)}
        {champ('Fonction', r.fonction)}
        {champ('E-mail', r.email)}
        {champ('Téléphone', r.phone)}
        {champ('Ville / Pays', [r.city, r.country].filter(Boolean).join(', '))}
        {champ('Déposée le', r.date)}
        {r.status === 'approved' && champ('Annuaire', r.correspondantCode ? `compte relié à ${nomsLies.get(r.correspondantCode)?.nom || r.correspondantCode}` : 'compte non relié (demande validée avant le 16/10/2026)')}
      </div>

      {estPersonnel(r) && r.status === 'pending' && (
        <div style={{ marginBottom: 14, padding: '10px 13px', borderRadius: 'var(--radius-md)', background: 'var(--amber-50)', border: '1px solid var(--amber-600)', fontSize: 12.5, lineHeight: 1.55 }}>
          <strong style={{ color: 'var(--amber-600)' }}>Demande d’un membre du personnel.</strong> Depuis le 16/10/2026, le compte d’un salarié
          s’ouvre depuis son dossier RH (Ressources → Personnel → « Ouvrir un compte »), relié à sa fiche. Refusez cette demande et prévenez la personne.
        </div>
      )}

      {enAttente && (
        <div style={{ marginBottom: 18, padding: '12px 13px', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-default)', background: 'var(--gray-25)' }}>
          <h4 style={{ margin: '0 0 6px', fontSize: 12.5, fontWeight: 700, color: 'var(--text-strong)' }}>Fiche de l’annuaire</h4>
          <p style={{ margin: '0 0 10px', fontSize: 12.5, lineHeight: 1.55, color: 'var(--text-muted)' }}>
            Le compte naît relié à une fiche de l’annuaire. Institution déclarée : <strong>{r.institution}</strong> ({r.typeLibelle || 'type non précisé'}).
          </p>
          {trouvees.echec && (
            <div style={{ marginBottom: 8, fontSize: 12.5, color: 'var(--red-600)' }}>L’annuaire n’a pas pu être lu : impossible de décider. Rechargez la page.</div>
          )}
          {trouvees.chargement && !trouvees.echec && <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>Recherche dans l’annuaire…</div>}
          {!trouvees.chargement && !trouvees.echec && (
            <div style={{ display: 'grid', gap: 6 }}>
              {candidats.length > 0 && (
                <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-strong)' }}>
                  {candidats.length === 1 ? 'Une fiche de l’annuaire porte un nom proche :' : 'Des fiches de l’annuaire portent un nom proche :'}
                </div>
              )}
              {candidats.map((c) => (
                <label key={c.code} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
                  <input type="radio" name={`lien-${r.id}`} checked={decisionEffective === c.code} onChange={() => setDecision(c.code)} />
                  <span>Relier à <strong>{c.nom}</strong>{c.actif === false ? ' (inactif)' : ''}
                    <span style={{ color: 'var(--text-muted)' }}> · {c.exact ? 'même nom' : `ressemblance ${Math.round(c.score * 100)} %`}</span>
                  </span>
                </label>
              ))}
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
                <input type="radio" name={`lien-${r.id}`} checked={decisionEffective === 'creer'} onChange={() => setDecision('creer')} />
                <span>Créer une nouvelle fiche <strong>{r.institution}</strong> dans l’annuaire, avec les coordonnées de la demande</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
                <input type="radio" name={`lien-${r.id}`} checked={!!autreChoisi} onChange={() => { /* se décide par le sélecteur ci-contre */ }} />
                <span>Relier à une autre fiche de l’annuaire :</span>
              </label>
              <div style={{ marginInlineStart: 24 }}>
                <ChoixCorrespondant id={`annuaire-${r.id}`} valeur={autreChoisi} onChange={(code) => setDecision(code || null)}
                  exclure={codesCandidats} vide="— chercher dans l’annuaire —"
                  aide={candidats.length ? 'Les fiches proposées ci-dessus n’y figurent pas.' : 'Aucune fiche de l’annuaire ne porte un nom proche de celui déclaré.'} />
              </div>
            </div>
          )}
        </div>
      )}

      {erreurSuivi && (
        <div style={{ marginBottom: 14, padding: '10px 13px', borderRadius: 'var(--radius-md)', background: 'var(--amber-50)', border: '1px solid var(--amber-600)', fontSize: 12.5, lineHeight: 1.55 }}>
          <strong style={{ color: 'var(--amber-600)' }}>Le suivi n’a pas pu être lu</strong> ({erreurSuivi}).
          <br />Cette fiche ne dit donc <strong>pas</strong> qu’il n’y a ni commentaire ni action : elle dit qu’elle ne sait pas.
        </div>
      )}

      <h4 style={{ margin: '0 0 9px', fontSize: 12.5, fontWeight: 700, color: 'var(--text-strong)' }}>Suivi</h4>
      <div style={{ display: 'grid', gap: 8, marginBottom: 14 }}>
        {notes?.length === 0 && !erreurSuivi && (
          <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>Aucun commentaire ni réponse pour l’instant.</div>
        )}
        {(notes || []).map((n) => (
          <div key={n.documentId} style={ligneSuivi}>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'baseline', marginBottom: 3 }}>
              <strong style={{ fontSize: 11.5, color: n.type === 'reponse' ? 'var(--cyan-700)' : n.type === 'entrant' ? 'var(--green-600)' : 'var(--text-muted)' }}>
                {n.type === 'reponse' ? 'Réponse envoyée' : n.type === 'entrant' ? 'Reçu du demandeur' : 'Commentaire interne'}
              </strong>
              <span style={{ fontSize: 11, color: 'var(--text-faint)' }}>{[n.auteur, horodate(n.createdAt)].filter(Boolean).join(' · ')}</span>
              {/* Le fait le plus important de la ligne : une réponse dont le
                  courriel a échoué existe en base mais n'est arrivée nulle part. */}
              {n.type === 'reponse' && n.envoi === 'echec' && (
                <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--red-600)' }}>— NON REÇUE (l’envoi a échoué)</span>
              )}
            </div>
            <div style={{ whiteSpace: 'pre-wrap' }}>{n.texte}</div>
          </div>
        ))}
      </div>

      {!!ref && (
        <div style={{ display: 'grid', gap: 8, marginBottom: 18 }}>
          <div style={{ display: 'flex', gap: 14, fontSize: 12.5 }}>
            {(['commentaire', 'reponse'] as const).map((t) => (
              <label key={t} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                <input type="radio" name="type-suivi" checked={type === t} onChange={() => setType(t)} />
                {t === 'commentaire' ? 'Commentaire interne' : `Réponse à ${r.email}`}
              </label>
            ))}
          </div>
          <textarea value={texte} onChange={(e) => setTexte(e.target.value)} rows={3}
            placeholder={type === 'reponse' ? 'Ce texte sera ENVOYÉ au demandeur.' : 'Note interne — le demandeur ne la voit pas.'}
            style={{ width: '100%', padding: '9px 11px', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-default)', fontFamily: 'var(--font-sans)', fontSize: 13, resize: 'vertical' }} />
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Bouton taille="sm" type="button" onClick={envoyer} disabled={occupeSuivi || !texte.trim()}>
              {type === 'reponse' ? 'Envoyer la réponse' : 'Ajouter le commentaire'}
            </Bouton>
          </div>
          {avis && <div style={{ fontSize: 12.5, lineHeight: 1.55, color: 'var(--text-body)' }}>{avis}</div>}
        </div>
      )}

      <h4 style={{ margin: '0 0 9px', fontSize: 12.5, fontWeight: 700, color: 'var(--text-strong)' }}>Journal</h4>
      <div style={{ display: 'grid', gap: 6 }}>
        {journal?.length === 0 && !erreurSuivi && (
          <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>Aucune action enregistrée sur cette demande.</div>
        )}
        {(journal || []).map((l, i) => (
          <div key={`${l.ts}-${i}`} style={{ ...ligneSuivi, background: 'var(--cyan-50)', borderColor: 'var(--cyan-200)' }}>
            <div style={{ fontSize: 11, color: 'var(--text-faint)', marginBottom: 2 }}>{[l.who, horodate(l.createdAt || l.ts)].filter(Boolean).join(' · ')}</div>
            <div>{l.detail}</div>
          </div>
        ))}
      </div>
    </Modale>
  );
}

/**
 * Le bloc entier, posé en tête de l'écran Utilisateurs.
 *
 * `onCompteChange` sert à une seule chose : une validation CRÉE un compte, et
 * la liste des comptes juste en dessous doit donc se relire. Sans ce rappel,
 * le compte tout juste créé n'apparaissait qu'au rechargement de la page.
 */
export default function DemandesCompte({ onCompteChange }: { onCompteChange: () => void }) {
  const [demandes, setDemandes] = useState<AccessRequest[] | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  // Ni succès ni échec : la décision a eu lieu, la personne n'en sait rien.
  const [alerte, setAlerte] = useState<string | null>(null);
  const [histoOuvert, setHistoOuvert] = useState(false);
  const [ouverte, setOuverte] = useState<string | null>(null);
  const busy = useBusy();

  const recharger = useCallback(() => {
    listRequests()
      .then((l) => { setDemandes(l); setErreur(null); })
      .catch((e) => {
        /* On NE repose PAS `[]` : la liste reste « non lue », et le bandeau le
           dit. Un échec avalé ici est indétectable — c'est exactement ce qui a
           laissé le 404 du proxy vivre sans être vu. */
        setDemandes(null);
        setErreur(e instanceof Error ? e.message : 'Erreur serveur');
      });
  }, []);
  useEffect(recharger, [recharger]);

  const enAttente = useMemo(() => (demandes || []).filter((r) => r.status === 'pending'), [demandes]);
  const traitees = useMemo(() => (demandes || []).filter((r) => r.status !== 'pending'), [demandes]);
  const fiche = useMemo(() => (demandes || []).find((r) => r.id === ouverte) || null, [demandes, ouverte]);

  /**
   * Annonce le résultat d'un geste QUI DÉCLENCHE UN COURRIEL.
   *
   * Valider comme refuser produisent leur effet AVANT l'envoi : le succès de
   * l'appel ne dit donc rien de ce que la personne a reçu. Vert seulement si
   * le message est parti ; sinon l'orange, qui nomme les deux faits.
   */
  const annoncer = (v: VerdictEnvoi, succes: string, echec: (motif: string) => string) => {
    setErreur(null);
    if (v.courrielEnvoye) { setAlerte(null); setInfo(succes); }
    else { setInfo(null); setAlerte(echec(v.motifEchec || 'motif inconnu')); }
  };

  const decider = (r: AccessRequest, geste: 'approve' | 'reject' | 'reopen', lien?: LienCarnet) => {
    setInfo(null); setAlerte(null); setErreur(null);
    const qui = `${r.prenom || ''} ${r.nom || ''}`.trim() || r.email;
    /* La clé du verrou porte la demande ET le geste : deux demandes
       différentes ne se bloquent pas l'une l'autre, mais un double clic sur le
       même bouton ne part qu'une fois. */
    void busy.run(`${geste}:${r.id}`, async () => {
      if (geste === 'approve') {
        /* Sans lien au carnet, on OUVRE la fiche : c'est là que la RQ le choisit.
           Valider depuis la liste, à l'aveugle, créerait un compte relié à rien. */
        if (!lien) { setOuverte(r.id); return; }
        const v = await approveRequest(r.id, profilCible(r), lien);
        recharger(); onCompteChange(); setOuverte(null);
        const ouCarnet = v.correspondantCode
          ? ` Compte relié à la fiche ${v.correspondantCode} de l’annuaire${v.correspondantCree ? ' (fiche créée)' : ''}.`
          : '';
        annoncer(v, `Demande de ${qui} validée : le lien de finalisation est parti à ${r.email}.${ouCarnet}`, (m) =>
          `La demande de ${qui} est validée et son compte est créé — mais le courriel de finalisation n’est PAS parti (${m}). `
          + `La personne ignore que sa demande a été acceptée et ne peut pas finaliser son inscription. `
          + `Prévenez-la, puis renvoyez-lui l’invitation depuis la liste des comptes une fois le service de courriel rétabli.`);
      } else if (geste === 'reject') {
        const v = await rejectRequest(r.id);
        recharger(); setOuverte(null);
        annoncer(v, `Demande de ${qui} refusée : la personne en a été informée par courriel.`, (m) =>
          `La demande de ${qui} est refusée — mais le courriel n’est PAS parti (${m}). La personne ne sait pas que sa `
          + `demande a été traitée et risque de la redéposer : prévenez-la autrement.`);
      } else {
        await reopenRequest(r.id);
        recharger(); setOuverte(null);
        /* Remettre en attente n'envoie RIEN, et c'est volontaire : le
           demandeur a déjà reçu un refus, lui annoncer que sa demande
           « repart » avant qu'on l'ait tranchée promettrait une issue. */
        setInfo(`Demande de ${qui} remise en attente. Aucun message n’a été envoyé au demandeur.`);
      }
    }).catch((e) => setErreur(e instanceof Error ? e.message : 'Action refusée'));
  };
  const occupe = busy.estOccupe();

  const total = (demandes || []).length;

  return (
    <section style={{ ...card, marginBottom: 22, padding: 0, overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '12px 16px', background: 'var(--cyan-50)', borderBottom: '1px solid var(--cyan-200)' }}>
        <I n="users" s={15} />
        <h3 style={{ margin: 0, fontSize: 13, fontWeight: 700, color: 'var(--cyan-700)' }}>Demandes de création de compte</h3>
        {enAttente.length > 0 && (
          <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: 19, height: 19, padding: '0 6px', borderRadius: 'var(--radius-pill)', background: 'var(--cyan-500)', color: 'var(--text-on-primary)', fontSize: 11, fontWeight: 700, fontFamily: 'var(--font-mono)' }}>{enAttente.length}</span>
        )}
        <span style={{ marginInlineStart: 'auto', fontSize: 11.5, color: 'var(--text-muted)' }}>
          {/* Le déplacement doit SE DIRE : quelqu'un qui cherchait ces demandes
              dans le site doit comprendre ici pourquoi elles n'y sont plus. */}
          Déposées depuis l’écran de connexion — elles se traitent ici, et nulle part ailleurs.
        </span>
      </div>

      {/* ⚠️ « PAS PU LIRE » SE DIT. C'est la moitié qui manquait : la route
          était fermée par le proxy, l'écran affichait une liste vide, et rien —
          ni bandeau, ni console, ni journal — ne distinguait les deux. Un état
          vide est une AFFIRMATION ; celle-ci était fausse. */}
      {demandes === null && erreur && (
        <div style={{ padding: '11px 16px', background: 'var(--amber-50)', borderBottom: '1px solid var(--amber-600)', fontSize: 12.5, lineHeight: 1.55 }}>
          <strong style={{ color: 'var(--amber-600)' }}>Les demandes n’ont pas pu être lues</strong> ({erreur}).
          <br />Cet écran ne dit donc <strong>pas</strong> qu’il n’y en a aucune : il dit qu’il ne sait pas. Les demandes déposées restent enregistrées.
        </div>
      )}
      {demandes !== null && erreur && (
        <div style={{ padding: '11px 16px', background: 'var(--red-50)', borderBottom: '1px solid var(--red-600)', fontSize: 12.5, color: 'var(--red-600)' }}>{erreur}</div>
      )}
      {info && <div style={{ padding: '11px 16px', background: 'var(--green-50)', borderBottom: '1px solid var(--green-600)', fontSize: 12.5, lineHeight: 1.55 }}>{info}</div>}
      {alerte && <div style={{ padding: '11px 16px', background: 'var(--amber-50)', borderBottom: '1px solid var(--amber-600)', fontSize: 12.5, lineHeight: 1.55 }}>{alerte}</div>}

      {demandes !== null && enAttente.length === 0 && (
        <EmptyState titre="Aucune demande en attente."
          indice={total > 0 ? `${total} demande${total > 1 ? 's' : ''} déjà traitée${total > 1 ? 's' : ''} — voir l’historique ci-dessous.` : 'Aucune demande n’a encore été déposée depuis l’écran de connexion.'} />
      )}

      {enAttente.map((r) => (
        <LigneDemande key={r.id} r={r} onOuvrir={() => setOuverte(r.id)} actions={
          <>
            {/* Ouvre la fiche : le lien au carnet s'y choisit avant d'inviter. */}
            <Bouton variante="primaire" taille="sm" type="button" disabled={occupe} onClick={() => setOuverte(r.id)}>Examiner &amp; inviter</Bouton>
            <Bouton variante="danger" taille="sm" type="button" disabled={occupe} onClick={() => decider(r, 'reject')}>Refuser</Bouton>
          </>
        } />
      ))}

      {traitees.length > 0 && (
        <>
          <button type="button" onClick={() => setHistoOuvert((v) => !v)}
            style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '10px 16px', borderTop: '1px solid var(--border-default)', background: 'var(--gray-25)', border: 'none', cursor: 'pointer', font: 'inherit', fontSize: 12.5, fontWeight: 600, color: 'var(--text-body)', textAlign: 'start' }}>
            <I n={histoOuvert ? 'chevron-bas' : 'chevron-right'} s={14} />
            Demandes traitées ({traitees.length})
            <span style={{ marginInlineStart: 'auto', fontWeight: 400, color: 'var(--text-muted)' }}>Validées ou refusées — une demande refusée peut être remise en attente.</span>
          </button>
          {histoOuvert && traitees.map((r) => (
            <LigneDemande key={r.id} r={r} onOuvrir={() => setOuverte(r.id)} actions={
              <>
                <Pastille statut={r.status} />
                {r.status === 'rejected' && (
                  <Bouton taille="sm" type="button" disabled={occupe} onClick={() => decider(r, 'reopen')}>Remettre en attente</Bouton>
                )}
              </>
            } />
          ))}
        </>
      )}

      {fiche && (
        <FicheDemande key={fiche.id} r={fiche} occupe={occupe}
          onClose={() => setOuverte(null)} onDecider={(g, lien) => decider(fiche, g, lien)} />
      )}
    </section>
  );
}
